/*!
 * cw-rum.js — Caroline Winters Real User Monitoring
 * Captures field Core Web Vitals (INP, LCP, CLS, FCP, TTFB) WITH attribution
 * from real shoppers and routes them to whatever sink is available.
 *
 * Depends on the self-hosted web-vitals ATTRIBUTION build exposing `window.webVitals`
 * (load `web-vitals.attribution.iife.js` BEFORE this file). No third-party CDN.
 *
 * Sink resolution (first available wins, plus console in debug):
 *   1. window.gtag(...)            → GA4 event per metric   (if GA4 present via web pixel/app)
 *   2. window.dataLayer.push(...)  → GTM                    (if GTM present)
 *   3. CW_RUM.endpoint             → navigator.sendBeacon   (only if YOU set an endpoint)
 *   4. console.table              → always, when debug mode is on
 *
 * Debug mode (zero backend needed — read your real INP off the live site):
 *   visit any page with  ?rum=debug   OR  run  localStorage.cw_rum_debug = '1'
 *   then interact + scroll; values print to the console as they finalise.
 *
 * Config: set window.CW_RUM = { endpoint, sampleRate, debug } BEFORE this script if needed.
 */
(function () {
  'use strict';

  if (window.__cwRumLoaded) return;          // run once
  window.__cwRumLoaded = true;

  var cfg = window.CW_RUM || {};
  var qs = new URLSearchParams(location.search);
  var DEBUG = cfg.debug === true
    || qs.get('rum') === 'debug'
    || (function () { try { return localStorage.getItem('cw_rum_debug') === '1'; } catch (e) { return false; } })();

  // Sampling — keep 1.0 pre-launch / low traffic; lower for high-traffic prod (e.g. 0.25).
  var SAMPLE = typeof cfg.sampleRate === 'number' ? cfg.sampleRate : 1.0;
  var sampledIn = Math.random() < SAMPLE;
  if (!sampledIn && !DEBUG) return;          // not in sample and not debugging → do nothing

  // The web-vitals attribution global must be present.
  var wv = window.webVitals;
  if (!wv || !wv.onINP) {
    if (DEBUG) console.warn('[cw-rum] window.webVitals not found — load web-vitals.attribution.iife.js first.');
    return;
  }

  // ---- page context (cheap, helps slice data later) -----------------------
  function pageType() {
    // Prefer a server-rendered hint if the theme sets one; else infer from path.
    if (window.CW_RUM && window.CW_RUM.pageType) return window.CW_RUM.pageType;
    var p = location.pathname;
    if (p === '/' || p === '') return 'home';
    if (p.indexOf('/products/') === 0) return 'product';
    if (p.indexOf('/collections/') === 0) return 'collection';
    if (p.indexOf('/cart') === 0) return 'cart';
    if (p.indexOf('/pages/') === 0) return 'page';
    return 'other';
  }
  var ctx = {
    page_type: pageType(),
    nav_type: (performance.getEntriesByType && performance.getEntriesByType('navigation')[0] || {}).type || 'unknown',
    device_memory: navigator.deviceMemory || null,
    effective_type: (navigator.connection || {}).effectiveType || null,
    viewport_w: window.innerWidth || null
  };

  // ---- the strongest attribution string web-vitals gives us per metric -----
  function attributionTarget(metric) {
    var a = metric.attribution || {};
    switch (metric.name) {
      case 'INP': return a.interactionTarget || a.interactionType || null; // the slow element/handler
      case 'LCP': return a.element || a.url || null;                       // the LCP element
      case 'CLS': return a.largestShiftTarget || null;                     // the biggest shifter
      default:    return null;                                            // FCP / TTFB: no element
    }
  }

  // ---- sinks ---------------------------------------------------------------
  function toGA4(m) {
    // GA4 convention (web.dev): event name = metric, value rounded (CLS ×1000), keep id+rating+delta.
    window.gtag('event', m.name, {
      value: m.name === 'CLS' ? Math.round(m.value * 1000) : Math.round(m.value),
      metric_id: m.id,
      metric_value: m.value,
      metric_delta: m.delta,
      metric_rating: m.rating,             // 'good' | 'needs-improvement' | 'poor'
      metric_target: attributionTarget(m),
      page_type: ctx.page_type,
      non_interaction: true
    });
  }
  function toGTM(m) {
    window.dataLayer.push({
      event: 'web-vitals', metric_name: m.name, metric_value: m.value,
      metric_id: m.id, metric_rating: m.rating, metric_target: attributionTarget(m),
      page_type: ctx.page_type
    });
  }

  // Batch raw beacons; flush once on the way out (sendBeacon survives unload).
  var queue = [];
  function toEndpoint(m) {
    queue.push({
      name: m.name, value: m.value, delta: m.delta, id: m.id, rating: m.rating,
      target: attributionTarget(m), page_type: ctx.page_type, url: location.pathname
    });
  }
  function flushEndpoint() {
    if (!cfg.endpoint || !queue.length) return;
    try {
      var payload = JSON.stringify({ ctx: ctx, metrics: queue, ts: Date.now() });
      navigator.sendBeacon(cfg.endpoint, new Blob([payload], { type: 'application/json' }));
      queue = [];
    } catch (e) { if (DEBUG) console.warn('[cw-rum] beacon failed', e); }
  }

  function report(metric) {
    if (DEBUG) {
      // One clear line per finalised metric — this is what you read on the live site.
      console.log(
        '%c[cw-rum] ' + metric.name + ' = ' +
        (metric.name === 'CLS' ? metric.value.toFixed(3) : Math.round(metric.value) + 'ms') +
        '  (' + metric.rating + ')  page=' + ctx.page_type +
        (attributionTarget(metric) ? '  ← ' + attributionTarget(metric) : ''),
        'color:' + (metric.rating === 'good' ? '#2e7d32' : metric.rating === 'poor' ? '#c62828' : '#ef6c00')
      );
    }
    if (typeof window.gtag === 'function') return toGA4(metric);
    if (Array.isArray(window.dataLayer)) return toGTM(metric);
    if (cfg.endpoint) return toEndpoint(metric);
    // else: console-only (debug) — no persistent sink configured. That's fine for a first read.
  }

  // ---- subscribe -----------------------------------------------------------
  // reportAllChanges:false → we get the FINAL value per metric (what p75/CrUX uses), not every change.
  var opts = { reportAllChanges: false };
  wv.onINP(report, opts);
  wv.onLCP(report, opts);
  wv.onCLS(report, opts);
  if (wv.onFCP) wv.onFCP(report, opts);
  if (wv.onTTFB) wv.onTTFB(report, opts);

  // Flush the endpoint queue when the page is hidden/unloaded.
  addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushEndpoint();
  });
  addEventListener('pagehide', flushEndpoint);

  if (DEBUG) console.log('[cw-rum] active · sink=' +
    (typeof window.gtag === 'function' ? 'GA4' :
     Array.isArray(window.dataLayer) ? 'GTM' :
     cfg.endpoint ? 'endpoint' : 'console-only') +
    ' · sample=' + SAMPLE + ' · page=' + ctx.page_type);
})();
